import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { register as authRegister } from '../../services/authService'
import { Eye, EyeOff, ArrowRight, Building2, CheckCircle2, ArrowLeft } from 'lucide-react'
import '../Login/styles.scss'

export default function Register() {
  const { login } = useAuth()
  const navigate = useNavigate()

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'USER',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.')
      setLoading(false)
      return
    }

    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.')
      setLoading(false)
      return
    }

    try {
      const data = await authRegister({
        username: form.email,
        fullName: form.fullName,
        email: form.email,
        password: form.password,
        role: form.role,
      })

      if (data.success) {
        login(data.token, data.user)
        navigate('/role-validation')
      } else {
        setError(data.message || 'Registration failed.')
      }
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  /* Password strength indicator */
  const getStrength = (pw) => {
    let score = 0
    if (pw.length >= 8) score++
    if (/[A-Z]/.test(pw)) score++
    if (/[0-9]/.test(pw)) score++
    if (/[^A-Za-z0-9]/.test(pw)) score++
    return score // 0-4
  }

  const strength = getStrength(form.password)
  const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong']
  const strengthColors = ['', '#EF4444', '#F59E0B', '#3B82F6', '#10B981']

  return (
    <div className="auth-page">
      {/* Left: Branding Panel */}
      <div className="auth-brand">
        <Link to="/" className="back-home-link">
          <ArrowLeft size={16} /> Home
        </Link>
        <div className="auth-brand__content">
          <Link to="/" className="auth-brand__logo" style={{ textDecoration: 'none' }}>
            <img src="/campusready_logo.svg" alt="CampusReady" />
            <span>CampusReady<span className="ai">.ai</span></span>
          </Link>
          <h1>Get Started in Minutes</h1>
          <p>Join universities worldwide that trust CampusReady to manage international admissions efficiently.</p>

          <div className="auth-brand__checklist">
            <div className="check-row"><CheckCircle2 size={16} color="#10B981" /><span>AI-powered document review</span></div>
            <div className="check-row"><CheckCircle2 size={16} color="#10B981" /><span>Real-time application tracking</span></div>
            <div className="check-row"><CheckCircle2 size={16} color="#10B981" /><span>Automated I-20 & visa management</span></div>
            <div className="check-row"><CheckCircle2 size={16} color="#10B981" /><span>Smart checklists & reminders</span></div>
          </div>
        </div>

        {/* Decorative dots */}
        <div className="auth-brand__dots" />
      </div>

      {/* Right: Registration Form */}
      <div className="auth-form-panel">
        <div className="auth-form-container">
          <div className="auth-form-header">
            <h2>Create your account</h2>
            <p>Start managing admissions smarter today</p>
          </div>

          {error && (
            <div className="auth-error">
              <span>{error}</span>
            </div>
          )}

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="fullName">Full name</label>
              <input
                id="fullName"
                type="text"
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
                placeholder="Sarah Johnson"
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="reg-email">Email address</label>
              <input
                id="reg-email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@university.edu"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="role">Role</label>
              <select
                id="role"
                name="role"
                value={form.role}
                onChange={handleChange}
              >
                <option value="USER">Student</option>
                <option value="AGENT">Recruitment Agent</option>
                <option value="STAFF">Admissions Staff</option>
                <option value="ADMIN">Administrator</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="reg-password">Password</label>
              <div className="password-wrap">
                <input
                  id="reg-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Min. 8 characters"
                  required
                />
                <button
                  type="button"
                  className="eye-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {form.password.length > 0 && (
                <div className="password-strength">
                  <div className="strength-bars">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="strength-bar"
                        style={{ background: i <= strength ? strengthColors[strength] : '#E2E8F0' }}
                      />
                    ))}
                  </div>
                  <span className="strength-label" style={{ color: strengthColors[strength] }}>
                    {strengthLabels[strength]}
                  </span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm password</label>
              <input
                id="confirmPassword"
                type="password"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter your password"
                required
              />
            </div>

            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? (
                <span className="spinner" />
              ) : (
                <>Create Account <ArrowRight size={16} /></>
              )}
            </button>
          </form>

          <p className="auth-footer-text">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
